/**
 * Adapters for using API location tree data with react-arborist.
 *
 * The API returns nodes with location_id and child; react-arborist defaults
 * to id and children. We use accessors so we can pass API data directly.
 */

import type { LocationTreeNode } from '../api/schedulingApis';

export const locationTreeIdAccessor = (d: LocationTreeNode): string =>
  String(d.location_id ?? '');

function floorIndexFromMeta(meta: Record<string, unknown> | null | undefined): number | null {
  if (!meta || typeof meta !== 'object') return null;
  const raw = meta.floor_number ?? meta.floorNumber;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Display order: floor siblings sort by floor index ascending; others keep source order. */
export function sortFloorSiblingsForDisplay(children: LocationTreeNode[]): LocationTreeNode[] {
  return children
    .map((node, originalIndex) => ({ node, originalIndex }))
    .sort((a, b) => {
      const aIsFloor = String(a.node.location_type ?? '').toLowerCase() === 'floor';
      const bIsFloor = String(b.node.location_type ?? '').toLowerCase() === 'floor';
      const aFloor = aIsFloor ? floorIndexFromMeta(a.node.meta) : null;
      const bFloor = bIsFloor ? floorIndexFromMeta(b.node.meta) : null;
      if (aFloor != null && bFloor != null) return aFloor - bFloor;
      return a.originalIndex - b.originalIndex;
    })
    .map(({ node }) => node);
}

export const locationTreeChildrenAccessor = (
  d: LocationTreeNode
): LocationTreeNode[] => d.child ?? [];

export const locationTreeManagementChildrenAccessor = (
  d: LocationTreeNode
): LocationTreeNode[] => sortFloorSiblingsForDisplay(d.child ?? []);
